const FORM_SELECTOR = ".eis-survey-form";
const QUESTION_SELECTOR = ".tc-eis-question-wrap";
const PENDING_CLASS = "eis-question-pending";
const REVEALED_CLASS = "eis-question-revealed";
const SCROLL_TOP_OFFSET = 120;
const SCROLL_FALLBACK_MS = 120;

let listenersBound = false;
let mutationObserver = null;
let syncTimer = null;
let scrollFallbackTimer = null;
let pendingScrollToIndex = null;
let suppressAutoScrollUntil = 0;
let surveyUserProgress = false;
let surveyMaxRevealed = 0;
let surveyActivePageId = null;
let surveyInitComplete = false;
let surveyHydratingUntil = 0;
const SURVEY_HYDRATION_MS = 1500;

function isQuestionAnswered(questionEl) {
  const radios = questionEl.querySelectorAll('input[type="radio"]');
  if (radios.length > 0) {
    return Array.from(radios).some((radio) => radio.checked);
  }

  const checkboxes = questionEl.querySelectorAll('input[type="checkbox"]');
  if (checkboxes.length > 0) {
    return Array.from(checkboxes).some((checkbox) => checkbox.checked);
  }

  const select = questionEl.querySelector("select");
  if (select && select.value !== "") {
    return true;
  }

  const textInputs = questionEl.querySelectorAll(
    'input[type="text"], input[type="number"], input[type="email"], textarea'
  );

  return Array.from(textInputs).some(
    (input) => input.value && input.value.trim() !== ""
  );
}

function getQuestionGroups(root = document) {
  const allQuestions = Array.from(root.querySelectorAll(QUESTION_SELECTOR));

  if (!allQuestions.length) {
    return [];
  }

  const pageHasForm = root.querySelector(FORM_SELECTOR);
  const groups = new Map();

  allQuestions.forEach((question) => {
    const form = question.closest(FORM_SELECTOR);
    const groupKey = form || pageHasForm || document.body;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey).push(question);
  });

  return Array.from(groups.entries()).map(([container, questions]) => ({
    container,
    questions,
  }));
}

function getPrimaryQuestions(root = document) {
  return getQuestionGroups(root)[0]?.questions || [];
}

function revealQuestionAtIndex(questionIndex) {
  const questions = getPrimaryQuestions();
  const target = questions[questionIndex];
  if (!target) {
    return;
  }

  target.classList.remove(PENDING_CLASS);
  target.classList.add(REVEALED_CLASS);
  persistMaxRevealed(questionIndex);
}

function getStoredMaxRevealed() {
  return surveyMaxRevealed >= 0 ? surveyMaxRevealed : 0;
}

function persistMaxRevealed(revealThroughIndex) {
  surveyMaxRevealed = Math.max(getStoredMaxRevealed(), revealThroughIndex);
}

function getConsecutiveAnsweredIndex(questions) {
  let lastAnsweredIndex = -1;

  for (let index = 0; index < questions.length; index += 1) {
    if (!isQuestionAnswered(questions[index])) {
      break;
    }
    lastAnsweredIndex = index;
  }

  return lastAnsweredIndex;
}

function markSurveyUserProgress() {
  if (Date.now() < surveyHydratingUntil) {
    return;
  }

  surveyUserProgress = true;
}

export function beginEisSurveyHydration(ms = SURVEY_HYDRATION_MS) {
  surveyHydratingUntil = Date.now() + ms;
  surveyUserProgress = false;
  surveyMaxRevealed = 0;
}

function applyInitialState(questions) {
  if (!questions.length) {
    return;
  }

  questions.forEach((question, index) => {
    question.dataset.eisIndex = String(index);
  });

  let revealThroughIndex = 0;

  if (surveyUserProgress) {
    revealThroughIndex = getStoredMaxRevealed();
    const lastConsecutiveAnswered = getConsecutiveAnsweredIndex(questions);

    if (lastConsecutiveAnswered >= 0) {
      revealThroughIndex = Math.max(
        revealThroughIndex,
        lastConsecutiveAnswered + 1
      );
    }
  }

  revealThroughIndex = Math.min(revealThroughIndex, questions.length - 1);
  persistMaxRevealed(revealThroughIndex);

  questions.forEach((question, index) => {
    const isRevealed = index <= revealThroughIndex;

    if (isRevealed) {
      question.classList.remove(PENDING_CLASS);
      question.classList.add(REVEALED_CLASS);
    } else {
      question.classList.add(PENDING_CLASS);
      question.classList.remove(REVEALED_CLASS);
    }
  });
}

export function resetEisSurveyProgress() {
  surveyUserProgress = false;
  surveyMaxRevealed = 0;
  surveyActivePageId = null;
}

function ensureSurveyPageScope(pageId) {
  if (pageId == null || pageId === "") {
    return;
  }

  if (String(surveyActivePageId) !== String(pageId)) {
    resetEisSurveyProgress();
    surveyActivePageId = pageId;
  }
}

export function shouldSkipScrollRestore() {
  return document.body.classList.contains("eis-survey-active");
}

export function isEisSurveyPage(root = document) {
  return Boolean(root?.querySelector?.(FORM_SELECTOR));
}

export function hasPendingEisScroll() {
  return pendingScrollToIndex !== null;
}

export function suppressEisAutoScroll(ms = 800) {
  suppressAutoScrollUntil = Date.now() + ms;
}

export function cancelPendingEisScroll() {
  pendingScrollToIndex = null;
  clearScrollFallback();
}

function shouldSuppressAutoScroll() {
  return Date.now() < suppressAutoScrollUntil;
}

function isFocusMovingToAnotherControl(relatedTarget) {
  if (!(relatedTarget instanceof Element)) {
    return false;
  }

  return relatedTarget.matches(
    'input:not([type="hidden"]), select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])'
  );
}

function getScrollTopOffset() {
  const header = document.querySelector(
    ".roi-header-main, .roi-header-transparent"
  );
  const headerHeight = header
    ? Math.ceil(header.getBoundingClientRect().height)
    : 0;
  return Math.max(SCROLL_TOP_OFFSET, headerHeight + 24);
}

function scrollToQuestion(questionEl) {
  if (!questionEl || !document.body.contains(questionEl)) {
    return;
  }

  const scrollTarget =
    questionEl.querySelector(".tc-eis-question h3") ||
    questionEl.querySelector(".tc-eis-question") ||
    questionEl;

  const offset = getScrollTopOffset();
  const rect = scrollTarget.getBoundingClientRect();
  const top = rect.top + window.pageYOffset - offset;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: "smooth",
  });
}

function clearScrollFallback() {
  if (scrollFallbackTimer) {
    window.clearTimeout(scrollFallbackTimer);
    scrollFallbackTimer = null;
  }
}

/** Run once after React commit — single smooth scroll, no interrupted retries. */
export function flushEisScrollAfterRender() {
  if (pendingScrollToIndex === null) {
    return;
  }

  const targetIndex = pendingScrollToIndex;
  pendingScrollToIndex = null;
  clearScrollFallback();

  window.requestAnimationFrame(() => {
    const target = getPrimaryQuestions()[targetIndex];
    if (!target) {
      return;
    }

    revealQuestionAtIndex(targetIndex);
    scrollToQuestion(target);
  });
}

function queueScrollToIndex(questionIndex, { force = false } = {}) {
  if (!force && shouldSuppressAutoScroll()) {
    return;
  }

  if (questionIndex < 0) {
    return;
  }

  const questions = getPrimaryQuestions();
  if (questionIndex >= questions.length) {
    return;
  }

  pendingScrollToIndex = questionIndex;
  revealQuestionAtIndex(questionIndex);

  clearScrollFallback();
  scrollFallbackTimer = window.setTimeout(() => {
    scrollFallbackTimer = null;
    flushEisScrollAfterRender();
  }, SCROLL_FALLBACK_MS);
}

function revealNextQuestionByIndex(currentIndex) {
  const nextIndex = currentIndex + 1;
  if (nextIndex >= getPrimaryQuestions().length) {
    return;
  }

  syncEisSurveyForms(document, surveyActivePageId);
  queueScrollToIndex(nextIndex);
}

function getQuestionsForTarget(target) {
  const questionEl = target.closest(QUESTION_SELECTOR);
  if (!questionEl) {
    return null;
  }

  const groups = getQuestionGroups();
  for (const group of groups) {
    const index = group.questions.indexOf(questionEl);
    if (index !== -1) {
      return { questionEl, questions: group.questions, index };
    }
  }

  return null;
}

function isUserInitiatedEvent(event) {
  return !event || event.isTrusted !== false;
}

function onDocumentChange(event) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }

  if (!isUserInitiatedEvent(event)) {
    return;
  }

  if (
    !target.matches(
      'input[type="radio"], input[type="checkbox"], select'
    )
  ) {
    return;
  }

  const match = getQuestionsForTarget(target);
  if (!match) {
    return;
  }

  if (target.matches('input[type="radio"]')) {
    markSurveyUserProgress();
    // Radio triggers a React re-render; scroll is flushed once in useLayoutEffect.
    syncEisSurveyForms(document, surveyActivePageId);
    queueScrollToIndex(match.index + 1, { force: true });
    return;
  }

  if (target.matches('input[type="checkbox"]')) {
    markSurveyUserProgress();
    revealNextQuestionByIndex(match.index);
    return;
  }

  if (target.matches("select")) {
    // ROI calculator dropdowns re-render in React — preserve scroll, no auto-advance.
    if (!document.querySelector(FORM_SELECTOR)) {
      return;
    }

    window.setTimeout(() => {
      if (shouldSuppressAutoScroll()) {
        return;
      }
      if (isQuestionAnswered(match.questionEl)) {
        markSurveyUserProgress();
        revealNextQuestionByIndex(match.index);
      }
    }, 50);
    return;
  }
}

function onDocumentBlur(event) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }

  if (!isUserInitiatedEvent(event)) {
    return;
  }

  if (
    !target.matches(
      'input[type="text"], input[type="number"], input[type="email"], textarea'
    )
  ) {
    return;
  }

  if (isFocusMovingToAnotherControl(event.relatedTarget)) {
    return;
  }

  const match = getQuestionsForTarget(target);
  if (!match) {
    return;
  }

  window.setTimeout(() => {
    if (shouldSuppressAutoScroll()) {
      return;
    }
    if (isQuestionAnswered(match.questionEl)) {
      markSurveyUserProgress();
      revealNextQuestionByIndex(match.index);
    }
  }, 50);
}

export function syncEisSurveyForms(root = document, pageId = null) {
  ensureSurveyPageScope(pageId);

  const groups = getQuestionGroups(root);

  groups.forEach(({ container, questions }) => {
    applyInitialState(questions);
    if (container instanceof HTMLElement && container.matches(FORM_SELECTOR)) {
      container.dataset.eisReady = "true";
    }
  });

  if (groups.length) {
    document.body.classList.add("eis-survey-active");
  }
}

/** Call after ROIPage re-render so scroll runs with fresh DOM nodes. */
export function onEisRadioAnswered(radioInput) {
  if (!(radioInput instanceof Element)) {
    return;
  }

  const match = getQuestionsForTarget(radioInput);
  if (!match) {
    return;
  }

  markSurveyUserProgress();
  queueScrollToIndex(match.index + 1, { force: true });
}

/** Queue scroll after radio click; actual scroll runs in flushEisScrollAfterRender. */
export function onEisRadioAnsweredByUuid(radioName) {
  const radio = document.querySelector(
    `input[type="radio"][name="${CSS.escape(radioName)}"]:checked`
  );

  if (radio) {
    onEisRadioAnswered(radio);
    return;
  }

  window.setTimeout(() => {
    const retryRadio = document.querySelector(
      `input[type="radio"][name="${CSS.escape(radioName)}"]:checked`
    );
    if (retryRadio) {
      onEisRadioAnswered(retryRadio);
    }
  }, 50);
}

function scheduleSync(root = document) {
  if (syncTimer) {
    window.clearTimeout(syncTimer);
  }

  syncEisSurveyForms(root);

  syncTimer = window.setTimeout(() => {
    syncEisSurveyForms(root);
  }, 100);
}

function bindGlobalListeners() {
  if (listenersBound) {
    return;
  }

  listenersBound = true;
  document.addEventListener("change", onDocumentChange, true);
  document.addEventListener("blur", onDocumentBlur, true);
}

function bindMutationObserver(root = document) {
  if (mutationObserver) {
    return;
  }

  let mutationTimer = null;
  mutationObserver = new MutationObserver(() => {
    if (mutationTimer) {
      window.clearTimeout(mutationTimer);
    }
    mutationTimer = window.setTimeout(() => {
      syncEisSurveyForms(root, surveyActivePageId);
    }, 150);
  });

  mutationObserver.observe(root.body || document.body, {
    childList: true,
    subtree: true,
  });
}

export function initEisSurveyForms(root = document) {
  bindGlobalListeners();
  bindMutationObserver(root);

  if (!surveyInitComplete) {
    surveyInitComplete = true;
    scheduleSync(root);
  }
}

export function destroyEisSurveyForms() {
  clearScrollFallback();
  pendingScrollToIndex = null;
  suppressAutoScrollUntil = 0;
  surveyUserProgress = false;
  surveyMaxRevealed = 0;
  surveyActivePageId = null;
  surveyInitComplete = false;
  surveyHydratingUntil = 0;

  if (syncTimer) {
    window.clearTimeout(syncTimer);
    syncTimer = null;
  }

  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }

  if (listenersBound) {
    document.removeEventListener("change", onDocumentChange, true);
    document.removeEventListener("blur", onDocumentBlur, true);
    listenersBound = false;
  }

  document.body.classList.remove("eis-survey-active");
}
